import React, {memo} from "react"
import {useTranslation} from "react-i18next";

import FormSection from "../../../components/FormSection";
import CollectionsTabList from "./CollectionsTabList";
import Model23MeasureField from "./Model23MeasureField";
import Model23Select from "./Model23Select";
import MeasurementsList from "./MeasurementsList";
import {formikPropertyChanged, generateMeasurementKey} from "./index";
import Button from "@mui/material/Button";


const computeDensity = (measurement) => {
    const weight = parseFloat(measurement.weight);
    const fillHeight = parseFloat(measurement.fillHeight);
    const samplingType = measurement.samplingType;

    if (isNaN(weight) || weight < 0) {
        return null;
    }

    if (samplingType === '1' || samplingType === '2'){
        // Vertical/Horizontal samples
        if (isNaN(fillHeight) || fillHeight <= 0) {
            return null;
        }
        return (weight / fillHeight) * 40;
    } else {
        return null;
    }
}


const DensityProfiles = memo(({
                                  formik,
                                  emptyProfile,
                                  emptyMeasure,
                                  model23Registry,
                                  stratigraphicProfileLayers = [],
                                  inputDisabled
                              }) => {
    const {t} = useTranslation(['models23', 'enums']);

    const onProfilesUpdate = (profiles) => {
        formik.setFieldValue('densityProfiles', profiles);
    }

    const onMeasurementsUpdate = (profileIndex, measurements) => {
        formik.setFieldValue(`densityProfiles[${profileIndex}].measurements`, measurements);
    }

    const createEmptyMeasure = (measureBefore, measureAfter) => {
        return {
            ...emptyMeasure,
            top: measureBefore?.bottom || null,
            bottom: measureAfter?.top || null,
            _key: generateMeasurementKey(),
        }
    }

    const handleCopyLayersFromStratigraphy = (profileIndex) => {
        const measurements = stratigraphicProfileLayers.map(layer => ({
            ...emptyMeasure,
            top: layer.top,
            bottom: layer.bottom,
            _key: generateMeasurementKey(),
        }))

        formik.setFieldValue(`densityProfiles[${profileIndex}].measurements`, measurements);
    }

    const renderProfile = (profileIndex) => {
        return <DensityProfile formik={formik}
                               model23Registry={model23Registry}
                               profileIndex={profileIndex}
                               createEmptyMeasure={createEmptyMeasure}
                               onMeasurementsUpdate={(measurements) => onMeasurementsUpdate(profileIndex, measurements)}
                               handleCopyLayersFromStratigraphy={() => handleCopyLayersFromStratigraphy(profileIndex)}
                               inputDisabled={inputDisabled}/>
    }

    return <FormSection title={t('model23Form.densityProfiles')}
                        openDefault={false}
                        error={Boolean(formik.errors.densityProfiles) && formik.touched.densityProfiles}>
        <CollectionsTabList collections={formik.values.densityProfiles}
                            renderCollection={renderProfile}
                            emptyCollection={emptyProfile}
                            onCollectionsUpdate={onProfilesUpdate}
                            inputDisabled={inputDisabled}/>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'densityProfiles', true) && prevProps.stratigraphicProfileLayers === nextProps.stratigraphicProfileLayers && prevProps.model23Registry === nextProps.model23Registry)


const DensityProfile = ({
                            formik,
                            profileIndex,
                            createEmptyMeasure,
                            onMeasurementsUpdate,
                            model23Registry,
                            handleCopyLayersFromStratigraphy,
                            inputDisabled
                        }) => {
    const {t} = useTranslation(['models23']);
    const densityProfileRegistry = model23Registry.data?.densityProfileRegistry;
    const samplingTypeRegistry = densityProfileRegistry?.samplingType;

    const profile = formik.values.densityProfiles[profileIndex];
    const profilePrefix = `densityProfiles[${profileIndex}]`

    const headers = [
        t('model23Form.densityProfile.samplingType'),
        t('model23Form.densityProfile.top'),
        t('model23Form.densityProfile.bottom'),
        t('model23Form.densityProfile.depth'),
        t('model23Form.densityProfile.fillHeight'),
        t('model23Form.densityProfile.weight'),
        t('model23Form.densityProfile.density')
    ]

    const handleChangeMeasurement = async (event, measurementIndex) => {
        await formik.handleChange(event);

        // Get the updated measurement
        const measurement = profile.measurements[measurementIndex];
        const name = event.target.name.split('.').pop(); // get the field name
        const updatedMeasurement = {...measurement, [name]: event.target.value};

        // Compute density
        const densityValue = computeDensity(updatedMeasurement);
        await formik.setFieldValue(`${profilePrefix}.measurements[${measurementIndex}].density`, densityValue);
    }

    const handleChangeSamplingType = async (event, measurementIndex) => {
        await formik.handleChange(event);

        const samplingType = event.target.value;
        if (samplingType==='1'){
            await formik.setFieldValue(`${profilePrefix}.measurements[${measurementIndex}].fillHeight`, 20);
        } else {
            await formik.setFieldValue(`${profilePrefix}.measurements[${measurementIndex}].fillHeight`, null);
        }
    }

    const renderMeasure = (row, index) => {
        const handleChange = (event) => handleChangeMeasurement(event, index);

        // Disable fillHeight only for Horizontal/Free samples (samplingType 1 and 3)
        const disableFillHeight = row.samplingType !== '2';

        return <>
            <Model23Select name={`${profilePrefix}.measurements[${index}].samplingType`}
                           label=""
                           labelFn={r => `${r.code} - ${r.description}`}
                           formik={formik}
                           registry={samplingTypeRegistry}
                           onChange={(event) => handleChangeSamplingType(event, index)}
                           disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].top`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 onChange={handleChange}
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].bottom`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 onChange={handleChange}
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].depth`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].fillHeight`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 onChange={handleChange}
                                 disabled={inputDisabled || disableFillHeight}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].weight`}
                                 unitOfMeasure="N"
                                 type="number"
                                 onChange={handleChange}
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`${profilePrefix}.measurements[${index}].density`}
                                 unitOfMeasure="kg/m3"
                                 type="number"
                                 disabled={inputDisabled}/>
        </>
    }

    return <MeasurementsList headers={headers}
                             measurements={profile.measurements}
                             createEmptyMeasure={createEmptyMeasure}
                             onMeasurementsUpdate={onMeasurementsUpdate}
                             renderMeasure={renderMeasure}
                             renderEmptyButton={() => <Button onClick={handleCopyLayersFromStratigraphy}
                                                              variant="gradient"
                                                              color='info'>
                                 {t('model23Form.densityProfile.copyLayersFromStratigraphy')}
                             </Button>}
                             inputDisabled={inputDisabled}
    />
}

export default DensityProfiles;